import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { LangContext } from '../App';
import { translations } from '../lib/translations';

const ACD_LOGO = '/acd-logo.png';
const ACD_URL = 'https://www.acd-advogados.net';

const LUIS_LOGO = '/luis-chaves-logo.png';
const LUIS_URL = 'https://luischavesadv.pt/';

const CARD_VARIANTS = {
  hidden: { opacity: 0, y: 25 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 + i * 0.15 },
  }),
};

function PartnerCard({ href, logo, logoAlt, name, subtitle, desc, visit, index }) {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`partner-${index}-link`}
      className="group flex flex-col items-center gap-6 border border-[#E5E0D8] p-8 sm:p-10 w-full hover:border-[#6B2D3E]/40 hover:shadow-md transition-all duration-400"
      custom={index}
      variants={CARD_VARIANTS}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      {/* Logo */}
      <div className="w-36 h-14 flex items-center justify-center">
        <img
          src={logo}
          alt={logoAlt}
          className="max-w-full max-h-full object-contain"
          data-testid={`partner-${index}-logo`}
        />
      </div>

      {/* Divider */}
      <div className="w-10 h-px bg-[#E5E0D8] group-hover:bg-[#6B2D3E]/30 transition-colors duration-400" />

      {/* Text */}
      <div className="text-center">
        <h3 className="font-serif text-lg text-[#1A1A1A] mb-1 tracking-tight">{name}</h3>
        <p className="font-sans text-xs tracking-[0.2em] uppercase text-[#6B2D3E] mb-4">{subtitle}</p>
        <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed mb-5">{desc}</p>
        <span className="inline-flex items-center gap-1.5 font-sans text-xs tracking-widest uppercase text-[#6B2D3E] group-hover:gap-2.5 transition-all duration-300">
          {visit}
          <ExternalLink size={11} />
        </span>
      </div>
    </motion.a>
  );
}

export default function Partners() {
  const lang = useContext(LangContext);
  const t = translations[lang].partners;

  return (
    <section
      id="partners"
      className="py-24 bg-white border-t border-[#E5E0D8]"
      data-testid="partners-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#6B2D3E] mb-5">
            — {t.sectionLabel} —
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] tracking-tight mb-4">
            {t.title}
          </h2>
          <p className="font-sans text-base text-[#4A4A4A] max-w-md mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Partner Cards — responsive 2-col grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
          <PartnerCard
            href={ACD_URL}
            logo={ACD_LOGO}
            logoAlt="ACD — Ana Cristina Domingues Advogados"
            name="ANA CRISTINA DOMINGUES"
            subtitle="Advogados"
            desc={t.acdDesc}
            visit={t.visit}
            index={0}
          />
          <PartnerCard
            href={LUIS_URL}
            logo={LUIS_LOGO}
            logoAlt="Dr. Luís Paulo Chaves Advogado"
            name="LUÍS PAULO CHAVES"
            subtitle="Advogado"
            desc={t.luisChavesDesc}
            visit={t.visit}
            index={1}
          />
        </div>
      </div>
    </section>
  );
}
